import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RocketOutlined,
  RobotOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import ProjectCard from '../ui/ProjectCard';
import ActivityItem from '../ui/ActivityItem';
import SideModal from '../shared/SideModal/SideModal';
import { useDashboard } from '../../hooks/useDashboard';
import { DashboardEvent, RecentService } from '../../services/dashboard.service';
import { getTechnologyImage } from '../../utils/serviceIconMapper';
import { getServiceDetailRoute, getMarketplaceProductByCategory } from '../../utils/serviceRouting';

// ---------- helpers ----------

interface QuickAction {
  key: string;
  title: string;
  description: string;
  path: string;
  images?: string[];
  icon?: React.ReactNode;
  iconBg?: string;
}

/** Mapea status del backend al formato que espera ProjectCard */
function mapServiceStatus(status: string): 'running' | 'deploying' | 'stopped' {
  const s = (status || '').toUpperCase();
  if (['RUNNING', 'ACTIVE', 'AVAILABLE', 'CREATE_COMPLETE'].includes(s)) return 'running';
  if (['CREATING', 'DEPLOYING', 'PENDING', 'UPDATING', 'CREATE_IN_PROGRESS'].includes(s)) return 'deploying';
  return 'stopped';
}

/** Mapea categoría a ruta de detalle (fuente única: utils/serviceRouting). */
function getDetailRoute(category: string, id: string): string {
  return getServiceDetailRoute(category, id);
}

/** Imagen por OS (VMs). Windows debe ir antes que Linux para no matchear "linux" en nombres genéricos. */
function getOSImagePath(osName: string): string | undefined {
  const os = (osName || '').toLowerCase();
  if (os.includes('windows')) return '/images/windows.png';
  if (os.includes('red hat') || os.includes('redhat') || os.includes('rhel')) return '/images/linux.png';
  if (os.includes('suse') || os.includes('sles') || os.includes('opensuse')) return '/images/suse.png';
  if (os.includes('debian')) return '/images/debian.png';
  if (os.includes('ubuntu')) return '/images/ubuntu.png';
  if (os.includes('amazon') || os.includes('amzn')) return '/images/amazon linux.png';
  if (os.includes('linux')) return '/images/linux.png';
  return undefined;
}

/** Todas las propiedades donde el backend puede enviar el OS de una VM (en orden de prioridad). */
function getVMOSDisplayName(svc: RecentService): string {
  return (
    svc.os ||
    svc.metadata?.os ||
    svc.metadata?.productOs ||
    svc.metadata?.product?.os ||
    svc.metadata?.product?.name ||
    svc.metadata?.productName ||
    svc.name ||
    svc.type ||
    ''
  );
}

/** Imagen por motor de base de datos */
const DB_IMAGES: Record<string, string> = {
  mysql: '/images/mysql.png',
  postgres: '/images/postgres.png',
  postgresql: '/images/postgres.png',
  mongodb: '/images/mongodb.png',
  mongo: '/images/mongodb.png',
  sqlserver: '/images/sqlserver.png',
  'sql server': '/images/sqlserver.png',
};

function getServiceCardSiteUrl(svc: RecentService): string | undefined {
  const cat = (svc.category || svc.type || '').toUpperCase();
  if (cat === 'APP_DEPLOY') return svc.endpointUrl || svc.publicDnsNameKubiy || undefined;

  return undefined;
}

/** Devuelve la URL del icono para la tarjeta según categoría, tecnología y OS */
function getServiceCardImage(svc: RecentService): string | undefined {
  const cat = (svc.category || svc.type || '').toUpperCase();
  const technology = (svc.technology || svc.metadata?.technology || svc.metadata?.appConfig?.type || '').toLowerCase();
  // OS de VM: varias fuentes (product.name, product.os, metadata.os, name del servicio, etc.)
  const osDisplayName = getVMOSDisplayName(svc);

  // Deploy Apps / SITE: icono por tecnología (HTML, Node, React, etc.)
  if (cat === 'APP_DEPLOY' || cat === 'SITE' || cat === 'APP_DEPLOY_STATIC' || cat === 'APP_DEPLOY_SERVER') {
    return getTechnologyImage(technology);
  }

  // VM / COMPUTE: icono por OS (Windows, Ubuntu, etc.) usando todas las propiedades posibles
  if (cat === 'VM' || cat === 'COMPUTE') {
    return getOSImagePath(osDisplayName) || '/images/linux.png';
  }

  // Database: icono por motor
  if (cat === 'DATABASE' || cat === 'DB') {
    const engine = (svc.metadata?.databaseConfig?.engine || '').toLowerCase();
    for (const [key, path] of Object.entries(DB_IMAGES)) {
      if (engine.includes(key)) return path;
    }
    return '/images/postgres.png'; // fallback
  }

  // Chatbot IA
  if (cat === 'AI_SERVICE' || cat === 'IA_SERVICE') {
    return '/images/IconoAssistant.webp';
  }

  if (cat === 'MONGODB') {
    return '/images/mongodb.png';
  }

  // Marketplace (WordPress, SQL Server, Ghost, RabbitMQ, etc.): usar el logoSrc
  // del catálogo como fuente de verdad — cubre todos los productos y evita
  // fallbacks con nombres mal armados (ej. SQLSERVER_EXPRESS -> sqlserver.png).
  const product = getMarketplaceProductByCategory(cat);
  if (product?.logoSrc) return product.logoSrc;

  return `/images/marketplace/${cat.toLowerCase()}.png`;
}

/** Mapea RecentService al formato de ProjectCard */
function mapToProjectCard(svc: RecentService) {
  const detailRoute = getDetailRoute(svc.category || svc.type, svc.id);
  return {
    id: svc.id,
    name: svc.name || svc.id,
    status: mapServiceStatus(svc.status),
    lastDeploy: formatTimeAgo(svc.updatedAt),
    type: svc.category || svc.type || 'Service',
    url: svc.endpointUrl || svc.publicDnsNameKubiy || undefined,
    siteUrl: getServiceCardSiteUrl(svc),
    detailRoute,
    technologyImage: getServiceCardImage(svc),
    // Deploy Apps (multi-app) => solo Ver detalles + Eliminar; el resto (VM, DB, marketplace) sí soporta ciclo de vida
    supportsLifecycle: !!detailRoute && !detailRoute.startsWith('/deploy-apps'),
  };
}

/** Mapea DashboardEvent al formato de ActivityItem */
function mapToActivityItem(ev: DashboardEvent) {
  const level = (ev.level || 'INFO').toUpperCase();
  let type: 'success' | 'deploying' | 'info' | 'error' = 'info';
  if (level === 'SUCCESS') type = 'success';
  else if (level === 'ERROR' || level === 'WARNING') type = 'error';
  else if (ev.message?.toLowerCase().includes('creating') || ev.message?.toLowerCase().includes('progress')) type = 'deploying';

  return {
    type,
    title: ev.message || 'Event',
    description: ev.description || ev.serviceName,
    time: formatTimeAgo(ev.createdAt),
    project: ev.serviceName,
  };
}

/** Convierte ISO date a "2h ago", "3d ago", etc. */
function formatTimeAgo(isoDate: string): string {
  if (!isoDate) return '';
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return `${Math.floor(diffD / 30)}mo ago`;
}

// ---------- Component ----------

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showHelpModal, setShowHelpModal] = useState(false);

  const { summary, activity, loading, error, refresh } = useDashboard();

  // Proyectos recientes
  const recentProjects = useMemo(() => {
    if (!summary?.recentServices) return [];
    return summary.recentServices.map(mapToProjectCard);
  }, [summary]);

  // Actividad reciente
  const activities = useMemo(() => {
    if (!activity?.events) return [];
    return activity.events.map(mapToActivityItem);
  }, [activity]);

  const deployAppImages = [
    getTechnologyImage('dotnet'),
    getTechnologyImage('nodejs'),
    getTechnologyImage('react'),
  ].filter(Boolean) as string[];
  const vmImages = ['/images/linux.png', '/images/windows.png'];
  const databaseImages = ['/images/postgres.png', '/images/mysql.png', '/images/mongodb.png'];

  const quickActions: QuickAction[] = [
    {
      key: 'deploy-app',
      title: t('dashboard.quickActions.deployApp.title'),
      description: t('dashboard.quickActions.deployApp.description'),
      images: deployAppImages,
      path: '/deploy-apps/create',
    },
    {
      key: 'vm',
      title: t('dashboard.quickActions.virtualMachine.title'),
      description: t('dashboard.quickActions.virtualMachine.description'),
      images: vmImages,
      path: '/resources/create-vm',
    },
    {
      key: 'database',
      title: t('dashboard.quickActions.database.title'),
      description: t('dashboard.quickActions.database.description'),
      images: databaseImages,
      path: '/resources/create-database',
    },
    {
      key: 'ai',
      title: t('dashboard.quickActions.aiComponent.title'),
      description: t('dashboard.quickActions.aiComponent.description'),
      // icon: <RobotOutlined className="text-lg" />,
      // icon: 'AI',
      path: '/chatbot-ia/create',
      iconBg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    },
    {
      key: 'marketplace',
      title: t('dashboard.quickActions.marketplace.title'),
      description: t('dashboard.quickActions.marketplace.description'),
      // icon: <AppstoreOutlined className="text-lg" />,
      // icon: 'MK',
      path: '/marketplace',
      iconBg: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    },
  ];

  // ---------- Loading state ----------
  if (loading && !summary) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-blue-500 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  // ---------- Error state ----------
  if (error && !summary) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {t('dashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6">
      <div className="mb-12">
        {/* <div className="flex items-center gap-2 mb-2">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.welcomeBack', { name: user?.name || 'User' })}
          </h1>
          <button
            onClick={() => setShowHelpModal(true)}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
            title={t('dashboard.helpTooltip')}
          >
            <InfoCircleOutlined className="text-lg" />
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors ml-1"
            title={t('dashboard.refreshTooltip')}
          >
            <ReloadOutlined className={`text-lg ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div> */}
        {/* <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('dashboard.overviewDashboard')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-2xl">
          {t('dashboard.manageInfrastructure')}
        </p> */}

        <div>
          {/* <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {t('dashboard.quickActions.title')}
          </h3> */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            {quickActions.map((action) => (
              <button
                key={action.key}
                onClick={() => navigate(action.path)}
                className="group text-left p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                {action.images ? (
                  <span className="inline-flex items-center -space-x-2">
                    {action.images.map((imageSrc: string) => (
                      <span
                        key={`${action.key}-${imageSrc}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full overflow-hidden border border-white dark:border-gray-700 bg-white dark:bg-gray-800"
                      >
                        <img src={imageSrc} alt={action.title} className="w-full h-full object-contain p-[1px]" />
                      </span>
                    ))}
                  </span>
                ) : (
                  // <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${action.iconBg}`}>
                  //   {action.icon}
                  // </span>
                  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg`}>
                   
                  </span>
                )}
                  <ArrowRightOutlined className="text-gray-400 group-hover:text-blue-600 transition-colors mt-1" />
                </div>
                <p className="mt-3 font-semibold text-gray-900 dark:text-white">{action.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{action.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('dashboard.recentProjects')}
            </h2>
            {/* <button
              onClick={() => navigate('/projects')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t('dashboard.viewAll')} →
            </button> */}
          </div>

          {recentProjects.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
              <RocketOutlined className="text-3xl text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">{t('dashboard.noServicesYet')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recentProjects.map((project, index) => (
                <ProjectCard key={index} {...project} onRefresh={refresh} />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('dashboard.recentActivity')}
            </h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            {activities.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">{t('dashboard.noRecentActivity')}</p>
            ) : (
              activities.map((activity, index) => (
                <ActivityItem key={index} {...activity} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      <SideModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={t('dashboard.help.title')}
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('dashboard.help.whatIs')}</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t('dashboard.help.whatIsDesc')}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('dashboard.help.keyFeatures')}</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>{t('dashboard.help.featureMetrics')}</li>
              <li>{t('dashboard.help.featureProjects')}</li>
              <li>{t('dashboard.help.featureActivity')}</li>
              <li>{t('dashboard.help.featureQuickActions')}</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('dashboard.help.gettingStarted')}</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              {t('dashboard.help.gettingStartedDesc1')}
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t('dashboard.help.gettingStartedDesc2')}
            </p>
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default Dashboard;